import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Check, X, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tone = { pending: "text-amber-300", approved: "text-emerald-300", denied: "text-white/40" };

const RequestRow = ({ r, onDecide }) => (
  <div className="flex flex-wrap items-center justify-between gap-4 p-5" data-testid={`admin-request-${r.id}`}>
    <div>
      <p className="text-base">{r.email}</p>
      <p className={`font-tech text-[10px] tracking-[0.3em] uppercase mt-1 ${tone[r.status]}`}>
        {r.status} · {new Date(r.created_at).toLocaleString()}
      </p>
    </div>
    <div className="flex gap-2">
      <button onClick={() => onDecide(r.id, "approve")} data-testid={`approve-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/50 text-emerald-300 px-4 py-2 font-tech text-[10px] tracking-[0.25em] uppercase hover:bg-emerald-400 hover:text-black transition-colors">
        <Check size={12} /> Approve
      </button>
      <button onClick={() => onDecide(r.id, "deny")} data-testid={`deny-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 text-white/60 px-4 py-2 font-tech text-[10px] tracking-[0.25em] uppercase hover:bg-white/10 transition-colors">
        <X size={12} /> Deny
      </button>
    </div>
  </div>
);

const KeyForm = ({ adminKey, setKey, busy, onSubmit }) => (
  <form onSubmit={onSubmit} className="mt-10 flex flex-col sm:flex-row gap-3" data-testid="admin-key-form">
    <input
      type="password"
      value={adminKey}
      onChange={(e) => setKey(e.target.value)}
      placeholder="Admin key"
      autoComplete="off"
      data-testid="admin-key-input"
      className="field-line flex-1 rounded-full border border-white/20 bg-black/40 px-6 py-4 text-base placeholder:text-white/30"
    />
    <button type="submit" disabled={busy || !adminKey} data-testid="admin-load-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF1493] text-black font-tech text-xs tracking-[0.25em] uppercase px-7 py-4 disabled:opacity-50">
      {busy && <RefreshCw size={14} className="animate-spin" />} Load
    </button>
  </form>
);

export default function AdminResume() {
  const [adminKey, setKey] = useState("");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { "X-Admin-Key": adminKey };

  const load = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/resume/requests`, { headers });
      setRows(data);
    } catch (err) {
      console.error("Admin load failed", err);
      toast.error("Invalid admin key.");
    } finally {
      setBusy(false);
    }
  };

  const decide = async (id, action) => {
    try {
      await axios.post(`${API}/resume/requests/${id}/${action}`, null, { headers });
      const status = action === "approve" ? "approved" : "denied";
      setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (err) {
      console.error("Admin decision failed", err);
      toast.error("Action failed.");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white px-6 py-16" data-testid="admin-resume-page">
      <div className="max-w-3xl mx-auto">
        <p className="font-tech text-[11px] tracking-[0.4em] uppercase text-[#FF66B2] flex items-center gap-2">
          <ShieldCheck size={13} strokeWidth={1.5} /> Crew Only
        </p>
        <h1 className="font-display font-black text-4xl md:text-5xl mt-4">Résumé <span className="italic text-[#FF1493]">requests.</span></h1>
        <KeyForm adminKey={adminKey} setKey={setKey} busy={busy} onSubmit={load} />
        {rows && (
          <div className="mt-10 rounded-3xl border border-white/10 divide-y divide-white/10" data-testid="admin-requests-list">
            {rows.length === 0 && <p className="p-8 font-tech text-xs tracking-[0.25em] uppercase text-white/40">No requests yet.</p>}
            {rows.map((r) => <RequestRow key={r.id} r={r} onDecide={decide} />)}
          </div>
        )}
      </div>
    </div>
  );
}
